import { LabelTemplate, FieldData, LabelSheetConfig, mmToPx } from '@/types/label';
import QRCode from 'qrcode';
import bwipjs from 'bwip-js';

// Generate a printable HTML page
export const generatePrintHtml = async (
  template: LabelTemplate,
  resolveContent: (content: string, data?: FieldData) => string,
  sheetConfig: LabelSheetConfig,
  data: FieldData[],
): Promise<string> => {
  const labelsPerPage = sheetConfig.columns * sheetConfig.rows;
  const totalPages = Math.ceil(data.length / labelsPerPage);
  
  // Generate all labels HTML
  const labelHtmlPromises = data.map(async (rowData) => {
    // Always use the provided resolver so Kiosk/Main editor behave consistently.
    return await renderLabelHtml(template, sheetConfig, (content) => resolveContent(content, rowData));
  });
  
  const labelHtmls = await Promise.all(labelHtmlPromises);
  
  // Build pages
  let pagesHtml = '';
  for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
    const pageLabels = labelHtmls.slice(
      pageIndex * labelsPerPage,
      (pageIndex + 1) * labelsPerPage
    );
    
    pagesHtml += `
      <div class="page" style="
        width: ${sheetConfig.pageWidth}mm;
        height: ${sheetConfig.pageHeight}mm;
        padding: ${sheetConfig.marginTop}mm ${sheetConfig.marginLeft}mm;
        box-sizing: border-box;
        page-break-after: ${pageIndex < totalPages - 1 ? 'always' : 'auto'};
      ">
        <div style="
          display: grid;
          grid-template-columns: repeat(${sheetConfig.columns}, ${sheetConfig.labelWidth}mm);
          gap: ${sheetConfig.verticalGap}mm ${sheetConfig.horizontalGap}mm;
        ">
          ${pageLabels.join('')}
        </div>
      </div>
    `;
  }
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Print Labels</title>
      <style>
        @page {
          size: ${sheetConfig.pageWidth}mm ${sheetConfig.pageHeight}mm;
          margin: 0;
        }
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        .label {
          position: relative;
          overflow: hidden;
          background: white;
        }
        .label-element {
          position: absolute;
        }
      </style>
    </head>
    <body>
      ${pagesHtml}
    </body>
    </html>
  `;
};

// Render a single label to HTML
const renderLabelHtml = async (
  template: LabelTemplate,
  sheetConfig: LabelSheetConfig,
  resolveContent: (content: string) => string
): Promise<string> => {
  const elements = await Promise.all(
    template.elements.map(async (element) => {
      if (element.visible === false) return '';
      
      const rotation = element.rotation || 0;
      const baseStyle = `
        position: absolute;
        left: ${element.position.x}mm;
        top: ${element.position.y}mm;
        width: ${element.size.width}mm;
        height: ${element.size.height}mm;
        ${rotation ? `transform: rotate(${rotation}deg); transform-origin: center center;` : ''}
      `;
      
      switch (element.type) {
        case 'text': {
          const content = resolveContent(element.content);
          // Convert font size from pt to a size that works in mm-based layout
          // 1pt = 0.352778mm, but we need to use pt in CSS for proper rendering
          const fontSize = element.fontSize;
          return `
            <div class="label-element" style="
              ${baseStyle}
              font-size: ${fontSize}pt;
              font-family: ${element.fontFamily}, Arial, sans-serif;
              font-weight: ${element.fontWeight};
              color: ${element.color || '#000'};
              text-align: ${element.textAlign};
              display: flex;
              align-items: center;
              justify-content: ${element.textAlign === 'center' ? 'center' : element.textAlign === 'right' ? 'flex-end' : 'flex-start'};
              overflow: hidden;
              word-break: break-word;
              line-height: 1.2;
              box-sizing: border-box;
            ">${escapeHtml(content)}</div>
          `;
        }
        
        case 'qrcode': {
          const content = resolveContent(element.content);
          try {
            const qrDataUrl = await QRCode.toDataURL(content || 'https://example.com', {
              width: Math.round(mmToPx(element.size.width, 300)),
              margin: 0,
              color: {
                dark: element.foregroundColor || '#000000',
                light: element.backgroundColor || '#ffffff',
              },
            });
            return `
              <img class="label-element" src="${qrDataUrl}" style="
                ${baseStyle}
                object-fit: contain;
              " />
            `;
          } catch {
            return `<div class="label-element" style="${baseStyle}">QR Error</div>`;
          }
        }
        
        case 'barcode': {
          const content = resolveContent(element.content);
          try {
            const canvas = document.createElement('canvas');
            // bwip-js' toCanvas is async in our typings; in practice it can be sync.
            // Awaiting covers both cases and prevents blank barcodes in print/PDF.
            await Promise.resolve(
              (bwipjs as unknown as { toCanvas: (c: HTMLCanvasElement, o: any) => any }).toCanvas(canvas, {
              bcid: element.barcodeType || 'code128',
              text: content || '12345678',
              scale: 3,
              height: 10,
              includetext: element.includeText,
              textxalign: 'center',
              })
            );
            return `
              <img class="label-element" src="${canvas.toDataURL()}" style="
                ${baseStyle}
                object-fit: contain;
              " />
            `;
          } catch {
            return `<div class="label-element" style="${baseStyle}">Barcode Error</div>`;
          }
        }
        
        case 'image': {
          return `
            <img class="label-element" src="${element.src}" style="
              ${baseStyle}
              object-fit: ${element.objectFit || 'contain'};
              border-radius: ${element.borderRadius || 0}mm;
            " />
          `;
        }
        
        case 'shape': {
          if (element.shapeType === 'circle') {
            return `
              <div class="label-element" style="
                ${baseStyle}
                border-radius: 50%;
                background: ${element.fillColor || 'transparent'};
                border: ${element.strokeWidth || 1}px solid ${element.strokeColor || '#000'};
              "></div>
            `;
          }
          return `
            <div class="label-element" style="
              ${baseStyle}
              background: ${element.fillColor || 'transparent'};
              border: ${element.strokeWidth || 1}px solid ${element.strokeColor || '#000'};
              border-radius: ${element.borderRadius || 0}mm;
            "></div>
          `;
        }
        
        case 'icon': {
          return `
            <div class="label-element" style="
              ${baseStyle}
              display: flex;
              align-items: center;
              justify-content: center;
              color: ${element.color || '#000'};
              font-size: ${Math.min(element.size.width, element.size.height) * 2}pt;
            ">⬡</div>
          `;
        }
        
        default:
          return '';
      }
    })
  );
  
  return `
    <div class="label" style="width: ${sheetConfig.labelWidth}mm; height: ${sheetConfig.labelHeight}mm;">
      ${elements.join('')}
    </div>
  `;
};

// Escape HTML to prevent XSS
const escapeHtml = (text: string): string => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

// Open print dialog with generated HTML
export const printLabels = async (
  template: LabelTemplate,
  resolveContent: (content: string, data?: FieldData) => string,
  sheetConfig: LabelSheetConfig,
  data: FieldData[],
): Promise<void> => {
  const html = await generatePrintHtml(template, resolveContent, sheetConfig, data);
  
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    throw new Error('Could not open print window. Please allow popups.');
  }
  
  printWindow.document.write(html);
  printWindow.document.close();

  // Wait for the print window to finish loading assets (images/fonts), otherwise some
  // browsers can produce a blank “Print to PDF”.
  await new Promise<void>((resolve) => {
    const timeout = printWindow.setTimeout(() => resolve(), 4000);
    const done = () => {
      printWindow.clearTimeout(timeout);
      resolve();
    };

    // DOM ready
    if (printWindow.document.readyState === 'complete') {
      // Also wait for images if any
      const images = Array.from(printWindow.document.images ?? []);
      if (images.length === 0) return done();
      let loaded = 0;
      const onOne = () => {
        loaded += 1;
        if (loaded >= images.length) done();
      };
      images.forEach((img) => {
        if (img.complete) onOne();
        else {
          img.addEventListener('load', onOne, { once: true });
          img.addEventListener('error', onOne, { once: true });
        }
      });
      return;
    }

    printWindow.addEventListener('load', done, { once: true });
  });

  printWindow.focus();
  // Some browsers are more reliable when printing happens in the next tick.
  await new Promise((r) => setTimeout(r, 0));
  printWindow.print();
  
  // Close after print dialog
  setTimeout(() => {
    printWindow.close();
  }, 1000);
};
